import { useState, useRef, useCallback, useEffect } from "react";
import { Flag, Printer } from "lucide-react";
import { playSuccess, playError } from "../helpers/audioPlayer";
import { useTranslation } from "../../i18n";
import { toast } from "react-toastify";
import HiddenScanInput from "../HiddenScanInput";
import ScanStatusIcons from "../ScanStatusIcons";
import ScanFlashOverlay from "../ScanFlashOverlay";
import FitWord from "../FitWord";
import type { DeskInfo, DispatchOrder } from "../../types/Dispatch";
import { scannableBarcodes } from "./itemBarcodes";
import { logDispatchEvent } from "./dispatchLog";
import { useDemoVisible } from "../helpers/demoMode";
import { recoverScanCaseReported } from "../helpers/scanCase";
import DispatchPrinterButton from "./dispatch-printer-button";
import DispatchReopenButton from "./dispatch-reopen-button";
import DispatchSessionBar from "./dispatch-session-bar";
import { DispatchSessionStats } from "./dispatch-session-chrome";
import "../../styles/dispatch-bench.css";

type Props = {
    deskInfo: DeskInfo;
    // May return a promise: the desk claims the order on the server first.
    onOrderFound: (order: DispatchOrder, scannedSku: string) => void | Promise<unknown>;
    goBack: () => void;
    onChangeDesk?: () => void;
    autoPrint?: boolean;
    onToggleAutoPrint?: () => void;
    lastOrder?: { order: DispatchOrder; boxes: import("../../types/Dispatch").DispatchBox[] } | null;
    onReopenOrder?: (data: { order: DispatchOrder; boxes: import("../../types/Dispatch").DispatchBox[] }) => void;
    // The orders contained in the scanned tote / sorted location. The item
    // scan is scoped to these — we only search the operator's tote, never the
    // whole backlog.
    toteOrders?: DispatchOrder[];
    // The open tote (or sorted location), shown as a chip in the session
    // bar; tapping it changes tote, the same as the back arrow here.
    toteLabel?: string;
    // The tote's last units are not there: flag every order still in it as
    // missing. The item scan is the only way into an order here, so without
    // this an order whose unit is missing could never be flagged.
    onFlagRemaining?: () => void;
};

const DispatchScanItem = ({ deskInfo, onOrderFound, goBack, onChangeDesk, autoPrint, onToggleAutoPrint, lastOrder, onReopenOrder, toteOrders, toteLabel, onFlagRemaining }: Props) => {
    const { t } = useTranslation();
    const DEMO_MODE = useDemoVisible();
    const [scan, setScan] = useState("");
    const [scanStatus, setScanStatus] = useState<"idle" | "success" | "error">("idle");
    const [loading, setLoading] = useState(false);
    // One demo SKU per order remaining in the scanned tote, so testers can
    // open each order without a hardware scanner.
    const [demoButtons, setDemoButtons] = useState<string[]>([]);
    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Demo buttons come from the tote's orders (one SKU each) so they always
    // resolve to an order in the operator's tote.
    useEffect(() => {
        if (!DEMO_MODE) return;
        const skus = (toteOrders ?? [])
            .map(o => o.items?.[0]?.sku)
            .filter((s): s is string => !!s)
            .slice(0, 8);
        setDemoButtons(skus);
    }, [DEMO_MODE, toteOrders]);

    const flashScanStatus = (status: "success" | "error") => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        setScanStatus(status);
        statusTimerRef.current = setTimeout(() => setScanStatus("idle"), 300);
    };

    const handleSubmit = useCallback(async (scannedValue?: string) => {
        const raw = (scannedValue ?? scan).trim();
        if (!raw || loading) return;

        setScan("");
        setLoading(true);
        // Case recovery against the tote's own orders — a closed set already
        // in memory, and the same wedge fault that mangles the pack screen
        // strands the operator here first. See scanCase.ts.
        const val = recoverScanCaseReported(
            raw,
            function* () {
                for (const o of toteOrders ?? []) {
                    for (const i of o.items ?? []) {
                        yield i.sku;
                        yield* scannableBarcodes(i);
                    }
                }
            }(),
            "Dispatch",
        ).value;
        try {
            // Tote-scoped: find the order in THIS tote whose items match the
            // scanned SKU/barcode. No backend round-trip — we already hold the
            // tote's orders, so we only ever search within the operator's tote.
            // When the item matches several orders, prefer one nobody holds at
            // a desk; if every match is claimed the desk's open-order answer
            // names the packer.
            const matches = (toteOrders ?? []).filter(o =>
                o.items?.some(i => i.sku === val || scannableBarcodes(i).includes(val)));
            const match = matches.find(o => !o.claimed_by) ?? matches[0];
            if (match) {
                playSuccess();
                await onOrderFound(match, val);
            } else {
                flashScanStatus("error");
                playError();
                toast.dismiss();
                toast.error(t("dispatch_item_not_in_tote"));
                // Success is logged by the parent's order-open row; only
                // the miss needs its own trail.
                logDispatchEvent({ event: "item-scan", barcode: val, result: "fail-not-in-tote" });
            }
        } catch {
            flashScanStatus("error");
            playError();
            toast.dismiss();
            toast.error(t("scan_failed"));
        }
        setLoading(false);
    }, [scan, loading, onOrderFound, toteOrders, t]);

    return (
        <div
            className="scan-panel dispatch-scan dispatch-bench"
            style={{
                backgroundColor:
                    scanStatus === "success" ? "rgba(34, 197, 94, 0.18)" :
                    undefined,
            }}
        >
                <ScanFlashOverlay active={scanStatus === "error"} />
            {/* Session bar: the desk and the open tote as labelled chips —
                this screen used to show neither, and its back arrow quietly
                meant "change tote". */}
            <DispatchSessionBar
                deskId={deskInfo.deskId}
                onChangeDesk={onChangeDesk}
                toteLabel={toteLabel}
                onChangeTote={goBack}
                showPoints
                extra={<>
                    {onFlagRemaining && toteOrders && toteOrders.length > 0 && (
                        <button
                            type="button"
                            className="dispatch-bar-btn dispatch-flag-btn"
                            onClick={onFlagRemaining}
                            aria-label={t("flag_remaining_title")}
                            title={t("flag_remaining_title")}
                        >
                            <Flag size={16} aria-hidden="true" />
                            {t("dispatch_flag_missing_short")}
                        </button>
                    )}
                    {onReopenOrder && <DispatchReopenButton lastOrder={lastOrder} onReopen={onReopenOrder} />}
                </>}
                onBack={goBack}
            />

            <div className="scan-centre">
                <ScanStatusIcons status={scanStatus === "error" ? "idle" : scanStatus} />

                <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                    {t("scan_item_dispatch").toUpperCase().split(" ").map((word, i) => (
                        <FitWord key={i}>{word}</FitWord>
                    ))}
                </div>
                {/* How many orders this tote still holds — the item scan
                    picks which one opens. */}
                {toteOrders && toteOrders.length > 0 && (
                    <div className={`scan-command-sub ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                        {t("dispatch_orders_in_tote", { count: String(toteOrders.length) })}
                    </div>
                )}

            </div>

            <HiddenScanInput
                value={scan}
                onChange={setScan}
                onSubmit={handleSubmit}
                disabled={loading}
                disableCamera
            />

            {/* Printer picker + auto-print toggle. The picker lives here (not
                just on the confirm screen) because auto-print fires the moment
                the confirm screen mounts — this is where the operator can
                still choose the printer. */}
            <div className="dispatch-scan-footer">
                <DispatchPrinterButton />
                {onToggleAutoPrint && (
                    <div className="dispatch-auto-print-toggle" onClick={onToggleAutoPrint}>
                        <Printer size={18} />
                        <span>{t("auto_print")}</span>
                        <div className={`dispatch-toggle ${autoPrint ? "dispatch-toggle--on" : ""}`}>
                            <div className="dispatch-toggle-knob" />
                        </div>
                    </div>
                )}
                <div className="dispatch-scan-footer-spacer" />
                <DispatchSessionStats />
            </div>

            {/* Demo button row — one per unfulfilled order in the operator's
                warehouse, populated from /orders so testers can open orders
                without a hardware scanner. */}
            {DEMO_MODE && demoButtons.length > 0 && (
                <div className="dispatch-demo-panel">
                    <span className="dispatch-demo-label">Demo</span>
                    {demoButtons.map(sku => (
                        <button key={sku} className="dispatch-demo-btn" onClick={() => handleSubmit(sku)} disabled={loading}>
                            {sku}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default DispatchScanItem;
